"use client";
import React, { useEffect, useRef } from "react";

interface Props {
  leftOffers: React.ReactNode;
  rightSidebar?: React.ReactNode;
  rightBanners?: React.ReactNode;
  hasBanners: boolean;
  hasSidebar: boolean;
  sidebarPosition?: "left" | "right";
}

export default function DynamicStickyLayout({
  leftOffers,
  rightSidebar,
  rightBanners,
  hasBanners,
  hasSidebar,
  sidebarPosition = "right",
}: Props) {
  const parentRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const leftInnerRef = useRef<HTMLDivElement>(null);
  const rightInnerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const setStatic = (el: HTMLDivElement) => {
      el.style.position = "static";
      el.style.top = "auto";
      el.style.bottom = "auto";
      el.style.width = "auto";
    };

    const setAbsoluteBottom = (el: HTMLDivElement) => {
      el.style.position = "absolute";
      el.style.top = "auto";
      el.style.bottom = "0";
      el.style.width = "100%";
    };

    const setFixed = (el: HTMLDivElement, top: number, width: number) => {
      el.style.position = "fixed";
      el.style.top = `${top}px`;
      el.style.bottom = "auto";
      el.style.width = `${width}px`;
    };

    const handleSticky = () => {
      if (!parentRef.current || !leftRef.current || !rightRef.current) return;
      if (!leftInnerRef.current || !rightInnerRef.current) return;

      if (window.innerWidth < 1024) {
        setStatic(leftInnerRef.current);
        setStatic(rightInnerRef.current);
        return;
      }

      const parentRect = parentRef.current.getBoundingClientRect();
      const leftContentHeight = leftInnerRef.current.scrollHeight;
      const rightContentHeight = rightInnerRef.current.scrollHeight;
      const leftWidth = leftRef.current.getBoundingClientRect().width;
      const rightWidth = rightRef.current.getBoundingClientRect().width;
      const topOffset = 112;

      if (leftContentHeight === 0 || rightContentHeight === 0) {
        setStatic(leftInnerRef.current);
        setStatic(rightInnerRef.current);
        return;
      }

      const parentTop = parentRect.top;
      const parentBottom = parentRect.bottom;

      // The shorter column gets sticky; the taller one scrolls naturally
      const makeLeftSticky = leftContentHeight <= rightContentHeight;

      if (makeLeftSticky) {
        setStatic(rightInnerRef.current);
        const stickyElementBottom = topOffset + leftContentHeight;
        if (parentTop > topOffset) {
          setStatic(leftInnerRef.current);
        } else if (parentBottom <= stickyElementBottom) {
          setAbsoluteBottom(leftInnerRef.current);
        } else {
          setFixed(leftInnerRef.current, topOffset, leftWidth);
        }
      } else {
        setStatic(leftInnerRef.current);
        const stickyElementBottom = topOffset + rightContentHeight;
        if (parentTop > topOffset) {
          setStatic(rightInnerRef.current);
        } else if (parentBottom <= stickyElementBottom) {
          setAbsoluteBottom(rightInnerRef.current);
        } else {
          setFixed(rightInnerRef.current, topOffset, rightWidth);
        }
      }
    };

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleSticky();
          ticking = false;
        });
        ticking = true;
      }
    };

    const observer = new ResizeObserver(() => handleSticky());
    if (leftInnerRef.current) observer.observe(leftInnerRef.current);
    if (rightInnerRef.current) observer.observe(rightInnerRef.current);

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", handleSticky);
    handleSticky();

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", handleSticky);
    };
  }, [hasBanners, hasSidebar, sidebarPosition]);

  const hasSidebarContent = hasSidebar || hasBanners;
  const isSidebarLeft = sidebarPosition === "left";

  return (
    <div
      ref={parentRef}
      className="flex flex-col lg:flex-row gap-6 lg:gap-7 items-stretch relative w-full"
    >
      {/* Sidebar on LEFT (30%) — rendered first in DOM so it appears left */}
      {isSidebarLeft && hasSidebarContent && (
        <div ref={leftRef} className="w-full lg:w-[30%] relative">
          <div ref={leftInnerRef} className="flex flex-col gap-6">
            {hasSidebar && rightSidebar}
            {hasBanners && rightBanners}
          </div>
        </div>
      )}

      {/* Offers column (70%) */}
      <div ref={isSidebarLeft ? rightRef : leftRef} className="w-full lg:w-[70%] relative">
        <div ref={isSidebarLeft ? rightInnerRef : leftInnerRef}>
          {leftOffers}
        </div>
      </div>

      {/* Sidebar on RIGHT (30%) — default behaviour */}
      {!isSidebarLeft && hasSidebarContent && (
        <div ref={rightRef} className="w-full lg:w-[30%] relative">
          <div ref={rightInnerRef} className="flex flex-col gap-6">
            {hasSidebar && rightSidebar}
            {hasBanners && rightBanners}
          </div>
        </div>
      )}
    </div>
  );
}
